import React, { createContext, useContext, useMemo, useEffect } from "react";
import {
  AppMode,
  PersistentState,
  Opportunity,
  TheProof,
  TheDMs,
  WorkflowTask,
  AICheckpoint,
  CandidateProfile,
  CandidateStory,
  SensitiveSupportProfile,
  TheBag,
  AppState,
} from "../types";
import { useMonyawnCore } from "../hooks/useMonyawnCore";
import { AppPage, Notice } from "./MonyawnContext.types";
import { MobileTab } from "../components/layout/MobileNavigation/MobileNavigation.contract";
import {
  getOpportunityProof,
  getOpportunityTasks,
  getOpportunityCheckpoints,
  getOpportunityDMs,
  getOpportunitySensitiveSupport,
  getOpportunityCandidateStory,
  getCompletionScore,
} from "../workflow";
import { validateAppStateIntegrity } from "../integrity";
import { buildOpportunityRecommendation, OpportunityRecommendation } from "../intelligence";
import { IntegrationHealth } from "../integrations";
import { buildAutomationPlan, AutomationCandidate } from "../automation";
import { FrictionSnapshot, getFrictionSnapshot } from "../lib/telemetry";
import { SessionProvider, useSession } from "./SessionContext";

interface MonyawnContextType {
  state: AppState; // Fused state for backward compatibility
  patchState: (updater: (current: AppState) => PersistentState, actionLabel?: string) => void;
  resetSeedState: () => void;
  setMode: (mode: AppMode) => void;
  setSelectedOpportunity: (id: string) => void;

  // From Session
  currentPage: AppPage;
  setCurrentPage: (page: AppPage) => void;
  mobileTab: MobileTab;
  setMobileTab: (tab: MobileTab) => void;
  notice: Notice;
  setNotice: (notice: Notice) => void;

  // Selection
  selectedAccount: any;
  selectedUser: any;
  selectedOpportunity: Opportunity | undefined;
  selectedProfile: CandidateProfile | undefined;
  selectedBag: TheBag | undefined;
  selectedCandidateStory: CandidateStory | undefined;
  selectedSensitiveSupport: SensitiveSupportProfile | undefined;

  // Filtered Lists
  opportunityProof: TheProof[];
  opportunityTasks: WorkflowTask[];
  opportunityCheckpoints: AICheckpoint[];
  opportunityDMs: TheDMs[];

  // Computed
  completionScore: number;
  integrityReport: { errors: string[]; warnings: string[] };
  lastIntegrityRunAt: string;
  recommendation: OpportunityRecommendation | null;
  automationPlan: AutomationCandidate[];
  integrationHealth: IntegrationHealth[];
  frictionSnapshot: FrictionSnapshot;
}

const MonyawnContext = createContext<MonyawnContextType | undefined>(undefined);

const MonyawnInternalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const session = useSession();
  const { state: persistentState, patchState, resetSeedState } = useMonyawnCore(session);
  const persistedSession = persistentState as Partial<AppState>;

  const effectiveSelectedAccountId =
    session.selectedAccountId ||
    persistedSession.selectedAccountId ||
    persistentState.accounts[0]?.account_id ||
    "";
  const effectiveSelectedUserId =
    session.selectedUserId || persistedSession.selectedUserId || persistentState.users[0]?.user_id || "";
  const effectiveSelectedOpportunityId =
    session.selectedOpportunityId ||
    persistedSession.selectedOpportunityId ||
    persistentState.opportunities[0]?.opportunity_id ||
    "";

  useEffect(() => {
    if (!session.selectedAccountId) {
      if (effectiveSelectedAccountId) {
        session.setSelectedAccount(effectiveSelectedAccountId);
      }
    }

    if (!session.selectedUserId) {
      if (effectiveSelectedUserId) {
        session.setSelectedUser(effectiveSelectedUserId);
      }
    }

    if (!session.selectedOpportunityId) {
      if (effectiveSelectedOpportunityId) {
        session.setSelectedOpportunity(effectiveSelectedOpportunityId);
      }
    }
  }, [
    effectiveSelectedAccountId,
    effectiveSelectedOpportunityId,
    effectiveSelectedUserId,
    session.selectedAccountId,
    session.selectedOpportunityId,
    session.selectedUserId,
    session.setSelectedAccount,
    session.setSelectedOpportunity,
    session.setSelectedUser,
  ]);

  // Fuse the states for backward compatibility with existing components
  const fusedState = useMemo<AppState>(
    () => ({
      ...persistentState,
      selectedAccountId: effectiveSelectedAccountId,
      selectedUserId: effectiveSelectedUserId,
      selectedOpportunityId: effectiveSelectedOpportunityId,
      currentMode: session.currentMode,
      lastSavedAt: session.lastSavedAt,
      lastExportedAt: session.lastExportedAt,
    }),
    [
      effectiveSelectedAccountId,
      effectiveSelectedOpportunityId,
      effectiveSelectedUserId,
      persistentState,
      session.currentMode,
      session.lastExportedAt,
      session.lastSavedAt,
    ],
  );

  const selectedAccount = useMemo(
    () => persistentState.accounts.find((a) => a.account_id === effectiveSelectedAccountId),
    [effectiveSelectedAccountId, persistentState.accounts],
  );

  const selectedUser = useMemo(
    () => persistentState.users.find((u) => u.user_id === effectiveSelectedUserId),
    [effectiveSelectedUserId, persistentState.users],
  );

  const selectedOpportunity = useMemo(
    () =>
      persistentState.opportunities.find((o) => o.opportunity_id === effectiveSelectedOpportunityId),
    [effectiveSelectedOpportunityId, persistentState.opportunities],
  );

  const selectedProfile = useMemo(
    () =>
      persistentState.candidateProfiles.find(
        (p) => p.opportunity_id === effectiveSelectedOpportunityId,
      ),
    [effectiveSelectedOpportunityId, persistentState.candidateProfiles],
  );

  const selectedBag = useMemo(
    () => persistentState.bags?.find((p) => p.opportunity_id === effectiveSelectedOpportunityId),
    [effectiveSelectedOpportunityId, persistentState.bags],
  );

  const selectedCandidateStory = useMemo(
    () =>
      selectedOpportunity
        ? getOpportunityCandidateStory(fusedState, selectedOpportunity.opportunity_id)
        : undefined,
    [selectedOpportunity, fusedState],
  );

  const selectedSensitiveSupport = useMemo(
    () =>
      selectedOpportunity
        ? getOpportunitySensitiveSupport(fusedState, selectedOpportunity.opportunity_id)
        : undefined,
    [selectedOpportunity, fusedState],
  );

  const opportunityProof = useMemo(
    () =>
      selectedOpportunity
        ? getOpportunityProof(fusedState, selectedOpportunity.opportunity_id)
        : [],
    [selectedOpportunity, fusedState],
  );

  const opportunityTasks = useMemo(
    () =>
      selectedOpportunity
        ? getOpportunityTasks(fusedState, selectedOpportunity.opportunity_id)
        : [],
    [selectedOpportunity, fusedState],
  );

  const opportunityCheckpoints = useMemo(
    () =>
      selectedOpportunity
        ? getOpportunityCheckpoints(fusedState, selectedOpportunity.opportunity_id)
        : [],
    [selectedOpportunity, fusedState],
  );

  const opportunityDMs = useMemo(
    () =>
      selectedOpportunity ? getOpportunityDMs(fusedState, selectedOpportunity.opportunity_id) : [],
    [selectedOpportunity, fusedState],
  );

  const completionScore = useMemo(
    () =>
      selectedOpportunity ? getCompletionScore(fusedState, selectedOpportunity.opportunity_id) : 0,
    [selectedOpportunity, fusedState],
  );

  const integrityReport = useMemo(() => validateAppStateIntegrity(fusedState), [fusedState]);
  const lastIntegrityRunAt = session.lastExportedAt || "";

  const recommendation = useMemo(
    () =>
      selectedOpportunity
        ? buildOpportunityRecommendation(fusedState, selectedOpportunity.opportunity_id)
        : null,
    [selectedOpportunity, fusedState],
  );

  const integrationHealth = useMemo(
    () => persistentState.integrationHealthSnapshot,
    [persistentState.integrationHealthSnapshot],
  );
  const automationPlan = useMemo(
    () => (recommendation ? buildAutomationPlan(recommendation, integrationHealth) : []),
    [integrationHealth, recommendation],
  );
  const frictionSnapshot = useMemo(() => getFrictionSnapshot(), [session.lastSavedAt]);

  // Phase 5: write intelligence outputs back into persisted state
  useEffect(() => {
    if (!recommendation) return;
    patchState(
      (current) => ({
        ...current,
        lastRecommendation: recommendation,
        automationQueue: automationPlan,
      }),
      "intelligence_snapshot",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendation, automationPlan]);

  const value = {
    state: fusedState,
    patchState,
    resetSeedState,
    setMode: session.setMode,
    setSelectedOpportunity: session.setSelectedOpportunity,

    // From Session
    currentPage: session.currentPage,
    setCurrentPage: session.setCurrentPage,
    mobileTab: session.mobileTab,
    setMobileTab: session.setMobileTab,
    notice: session.notice,
    setNotice: session.setNotice,

    selectedAccount,
    selectedUser,
    selectedOpportunity,
    selectedProfile,
    selectedBag,
    selectedCandidateStory,
    selectedSensitiveSupport,
    opportunityProof,
    opportunityTasks,
    opportunityCheckpoints,
    opportunityDMs,
    completionScore,
    integrityReport,
    lastIntegrityRunAt,
    recommendation,
    automationPlan,
    integrationHealth,
    frictionSnapshot,
  };
  return <MonyawnContext.Provider value={value}>{children}</MonyawnContext.Provider>;
};

export const MonyawnProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <SessionProvider>
      <MonyawnInternalProvider>{children}</MonyawnInternalProvider>
    </SessionProvider>
  );
};

export const useMonyawn = () => {
  const context = useContext(MonyawnContext);
  if (!context) throw new Error("useMonyawn must be used within MonyawnProvider");
  return context;
};
